import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow, Reveal } from "../components/Reveal";

const Panel = ({ layer, i, progress }) => {
  const y = useTransform(progress, [0, 1], [60 * (i + 1), -60 * (i + 1)]);
  const offsets = ["ms-0", "ms-[8%]", "ms-[16%]"];
  return (
    <motion.div style={{ y }} data-testid={`human-layer-${i}`} className={`relative w-[84%] ${offsets[i]} ${i ? "-mt-10 md:-mt-14" : ""}`}>
      <div className="glass-panel rounded-[28px] p-7 md:p-9" style={{ zIndex: 3 - i }}>
        <div className="flex items-center justify-between">
          <span className="font-display text-2xl text-noor-pearl md:text-3xl">{layer.k}</span>
          <span className="font-brand text-xs text-noor-champagne" dir="ltr">L{i + 1}</span>
        </div>
        <p className="mt-3 text-[15px] text-noor-mist">{layer.t}</p>
        <div className="mt-6 flex gap-1.5">
          {Array.from({ length: 18 }).map((_, k) => (
            <span key={k} className="h-6 w-full rounded-full bg-noor-champagne" style={{ opacity: 0.08 + ((k * (i + 2)) % 7) * 0.05 * (i + 1) }} />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const Human = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0.2, 0.9, 0.3]);
  return (
    <section ref={ref} id="about" data-testid="human-section" className="relative z-[2] overflow-hidden bg-noor-deep py-28 md:py-44">
      <motion.div style={{ opacity: glow }} className="pointer-events-none absolute end-[5%] top-1/3 h-[60vh] w-[60vh] rounded-full bg-noor-gold/25 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Eyebrow>{t.human.eyebrow}</Eyebrow>
          <MaskLines lines={t.human.lines} className="display-lg mt-6 font-display text-noor-pearl" lineClassName="italic-accent text-noor-champagne" testId="human-headline" />
          <Reveal as="p" className="mt-8 max-w-lg text-base leading-relaxed text-noor-mist">{t.human.body}</Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          {t.human.layers.map((l, i) => (
            <Panel key={l.k} layer={l} i={i} progress={scrollYProgress} />
          ))}
        </div>
      </div>
    </section>
  );
};
